import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { getModelPricing } from '@/utils/model_pricing';
import ModelPriceTier from './ModelPriceTier';

function usageDescription(model) {
  const output = model?.capabilities?.output || {};
  if (output.hasVideo) return 'Duration, resolution, audio, and the number of videos affect the final price.';
  if (output.hasImages) return 'Image size, quality, and the number of images affect the final price. Editing and reference images can also affect usage.';
  if (output.hasAudio) return 'Audio length, the amount of text, and selected settings affect the final price.';
  if (output.has3D) return 'Generation settings, textures, and additional processing affect the final price.';
  if (output.hasEmbeddings) return 'The amount of text processed affects the final price.';
  return 'Conversation length, replies, reasoning, and tool use affect the final price. Longer conversations and more work can use more credits.';
}

export default function ModelCreditDetails({ model, compact = false }) {
  const { colors } = useTheme();
  const price = getModelPricing(model);
  if (!price) return null;

  return (
    <View style={[styles.card, compact && styles.compactCard, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
      <View style={styles.priceRow}>
        <Text size="s" color="secondary" weight="medium">Starts at</Text>
        <Text size="l" weight="bold" style={styles.amount}>{price.credits}</Text>
      </View>
      <Text size="s" color="secondary" style={styles.copy}>
        The starting price for this model. Your final charge depends on actual usage and selected options.
      </Text>
      {model.videoPromotion?.enabled && <Text size="s" weight="semibold" style={styles.copy}>
        Video special: 10% off generation credits. The discount is already included in your price.
      </Text>}
      {!compact && <View style={styles.tierRow}>
        <ModelPriceTier model={model} />
        <Text size="xs" color="tertiary" style={{ flex: 1 }}>More $ signs mean a higher starting credit cost.</Text>
      </View>}
      <View style={[styles.section, compact && styles.compactSection, { borderColor: colors.border_color_secondary }]}>
        <Text size="s" weight="semibold">What affects the price</Text>
        <Text size="s" color="secondary" style={styles.copy}>{usageDescription(model)}</Text>
      </View>
      <View style={[styles.section, compact && styles.compactSection, { borderColor: colors.border_color_secondary }]}>
        <Text size="s" weight="semibold">Temporary holds</Text>
        <Text size="s" color="secondary" style={styles.copy}>
          Credits are reserved while a request runs. The hold can grow as work continues. Unused credits are released; the hold is not an extra charge.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: 12, padding: 18, gap: 8 },
  compactCard: { padding: 16, gap: 6, borderRadius: 16 },
  compactSection: { marginTop: 2, paddingTop: 10 },
  priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  amount: { fontVariant: ['tabular-nums'] },
  tierRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  copy: { lineHeight: 20 },
  section: { borderTopWidth: StyleSheet.hairlineWidth, marginTop: 6, paddingTop: 14, gap: 5 },
});
